import { marked } from 'marked';

import { fixGluedEmphasis } from './fix-glued-emphasis';
import { sanitizeHtml } from './sanitize-html';

// AI-generated markdown becomes editor HTML; blank lines need an explicit
// empty <p> since the editor's paragraphs carry no margin.
const BLOCK_BOUNDARY =
  /(<\/(?:p|ul|ol|blockquote|h[1-6])>)\s*(?=<(?:p|ul|ol|blockquote|h[1-6])[\s>])/gi;

const NBSP = /\u00A0|&nbsp;/g;

// Bold-italic first: `\S` in the bold rule below matches `*`, so running that
// one on `***x***` would eat one marker and glue the leftovers into the text.
// `marked` gets this right on the other branch; the two must agree.
// The span is bounded: `[\s\S]*?` makes every unpaired `**` scan to the end of
// its text run, which is quadratic in the run length - measured 109 ms at
// 20 KB but 7.7 s at 160 KB on a run of `**a ` repeats, which would freeze the
// tab. The input is model output and nothing caps it. Emphasis never
// legitimately spans 2 K characters, and the bound is byte-identical to the
// unbounded version on every case in the test file.
const MD_BOLD_ITALIC = /\*\*\*(?=\S)([\s\S]{0,2000}?\S)\*\*\*/g;

// Non-greedy, and anchored on non-space either side so `** ` or a lone `**`
// is left alone.
const MD_BOLD = /\*\*(?=\S)([\s\S]{0,2000}?\S)\*\*/g;

// A tag, an HTML comment, a run of text, or a stray `<`. The tag alternative
// has to *start* like a tag: descriptions really do say "surface < 100 m2 …
// -> à voir", and anything looser turns that pair into a pseudo-tag and skips
// every marker between them - the exact case this rewrite exists for. A `<`
// that begins nothing is consumed alone and left as is.
// Comments get their own alternative: falling through to the text branch would
// have the sanitizer render `!-- note --&gt;` on screen.
// Known limit: a `>` inside a quoted attribute value ends the tag alternative
// early, so text after it in that same tag is treated as content. Rare in
// generated output, and the sanitizer runs after this either way.
const TAG_OR_TEXT = /<\/?[a-zA-Z][^<>]*>|<!--[\s\S]*?-->|[^<]+|</g;

// The generator is told to bold with `**` (generate-cma-description.ts) and is
// not told to avoid HTML, so it regularly answers with HTML blocks carrying
// markdown emphasis inside them. That mix used to take the verbatim branch
// below and render the markers literally - and the damage latched, because once
// the editor serializes such a value it starts with `<p>` forever, which is the
// branch `MarkdownComposer.seedEditor` never re-reads as markdown.
// Only emphasis contained in a single text run is converted; a `**` spanning a
// tag boundary is left alone rather than guessed at.
const boldSurvivingMarkdown = (html: string): string =>
  html.replace(TAG_OR_TEXT, segment => {
    // A `<` that opened nothing ("surface < 100 m2"). Escape it rather than
    // pass it through: the sanitizer's own tag matcher is looser than the
    // tokenizer above and only needs a closing `>` to fire, so leaving the
    // `<` raw lets a `<strong>` injected further along the run supply that
    // `>` and turn inert text into markup - `<p><form action=javascript:…
    // **b**</p>` renders as text today and would become a real <form>.
    // Escaping also stops an unterminated `<!--` from swallowing the words
    // after it, and is what the editor should have received anyway.
    if (segment === '<') {
      return '&lt;';
    }
    return segment.startsWith('<')
      ? segment
      : segment
          .replace(MD_BOLD_ITALIC, '<em><strong>$1</strong></em>')
          .replace(MD_BOLD, '<strong>$1</strong>');
  });

export const generatedTextToEditorHtml = (text: string): string => {
  if (/^\s*</.test(text)) {
    return sanitizeHtml(boldSurvivingMarkdown(text));
  }
  const html = marked.parse(fixGluedEmphasis(text), {
    async: false,
    breaks: true,
  }) as string;
  const spaced = html.replace(BLOCK_BOUNDARY, '$1<p></p>');
  return sanitizeHtml(spaced).replace(NBSP, ' ').trim();
};
